import { EventType } from "@/analytics/analytics-events";
import {
  ToggleTrajectoriesVisibilityProperties,
  ToggleWaypointHeightProperties,
  ToggleWaypointsVisibilityProperties,
} from "@/analytics/viewer-settings-events-properties";
import { useSettingsContext } from "@/components/common/settings-context/settings-context";
import {
  useCurrentScene,
  useWaypointAltitudeRange,
  useWaypointDateRange,
} from "@/modes/mode-data-context";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import {
  selectObjectsVisibility,
  selectShouldShowWaypointsOnFloors,
} from "@/store/view-options/view-options-selectors";
import {
  setObjectVisibility,
  setShouldShowWaypointsOnFloors,
  ViewObjectTypes,
  WaypointsColoringOptions,
} from "@/store/view-options/view-options-slice";
import { useLocalStorage } from "@faro-lotv/app-component-toolbox";
import { FaroMenu, FaroMenuProps, FaroText, grey } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { Divider, Stack } from "@mui/material";
import { isEqual } from "es-toolkit";
import { useCallback, useEffect, type JSX } from "react";
import { ShowWaypointsOnFloorsHelp } from "../help-popover";
import { OrientationOf2dOverviewMenu } from "./2d-overview-orientation-options";
import { CadDistanceFilteringSlider } from "./cad-distance-filtering-slider";
import { GuidedSplatsNavigationToggle } from "./guided-splats-navigation-toggle";
import { OdometryWaypointSpacingSlider } from "./odometry-waypoint-spacing-slider";
import { PointCloudProfilesSlider } from "./point-cloud-profiles-slider";
import { ViewOptionSection } from "./view-options-section";
import { ViewOptionSwitch } from "./view-options-switch";
import { VisibilityDistanceSlider } from "./visibility-distance-slider";
import { WaypointsColorizationOptions } from "./waypoints-colorization-options";

type ViewOptionsMenuProps = Pick<
  FaroMenuProps,
  "open" | "anchorEl" | "onClose"
>;

/** @returns a PopOver to open from a menu to control the app view options */
export function ViewOptionsMenu({
  open,
  anchorEl,
  onClose,
}: ViewOptionsMenuProps): JSX.Element {
  const dispatch = useAppDispatch();

  const objectsVisibility = useAppSelector(selectObjectsVisibility, isEqual);

  // Synchronize waypoints labels visibility with the local storage of the browser
  const [showWaypointLabels, setShowWaypointLabels] = useLocalStorage(
    "showWaypointLabels",
    objectsVisibility[ViewObjectTypes.waypointLabels],
  );
  useEffect(() => {
    if (
      objectsVisibility[ViewObjectTypes.waypointLabels] !== showWaypointLabels
    ) {
      dispatch(
        setObjectVisibility({
          type: ViewObjectTypes.waypointLabels,
          visibility: showWaypointLabels,
        }),
      );
    }
  }, [dispatch, objectsVisibility, showWaypointLabels]);

  // Synchronize trajectories visibility with the local storage of the browser
  // By default the trajectories are visible
  const [showTrajectories, setShowTrajectories] = useLocalStorage(
    "showTrajectories",
    true,
  );
  useEffect(() => {
    if (objectsVisibility[ViewObjectTypes.trajectories] !== showTrajectories) {
      dispatch(
        setObjectVisibility({
          type: ViewObjectTypes.trajectories,
          visibility: showTrajectories,
        }),
      );
    }
  }, [dispatch, objectsVisibility, showTrajectories]);

  const shouldShowWaypointsOnFloors = useAppSelector(
    selectShouldShowWaypointsOnFloors,
  );

  const updateObjectVisibility = useCallback(
    (visible: boolean, type: ViewObjectTypes, eventToTrack: EventType) => {
      Analytics.track<ToggleWaypointsVisibilityProperties>(eventToTrack, {
        visible,
      });

      dispatch(setObjectVisibility({ type, visibility: visible }));
    },
    [dispatch],
  );

  const toggleShouldShowWaypointsOnFloors = useCallback(
    (enabling: boolean) => {
      Analytics.track<ToggleWaypointHeightProperties>(
        EventType.toggleWaypointHeight,
        {
          enabling,
        },
      );

      dispatch(setShouldShowWaypointsOnFloors(enabling));
    },
    [dispatch],
  );

  const waypointsAltitudeRange = useWaypointAltitudeRange();
  const waypointsDateRange = useWaypointDateRange();

  const { waypointsColoring, setWaypointsColoring } = useSettingsContext();

  // A disabled colorization option can never be selected:
  useEffect(() => {
    if (!open) return;
    if (
      waypointsColoring === WaypointsColoringOptions.byElevation &&
      !waypointsAltitudeRange
    ) {
      setWaypointsColoring(WaypointsColoringOptions.custom);
    } else if (
      waypointsColoring === WaypointsColoringOptions.byCaptureDate &&
      !waypointsDateRange
    ) {
      setWaypointsColoring(WaypointsColoringOptions.custom);
    }
  }, [
    setWaypointsColoring,
    waypointsColoring,
    dispatch,
    waypointsAltitudeRange,
    waypointsDateRange,
    open,
  ]);

  const { panos, paths } = useCurrentScene();
  const hasPanos = panos.length > 0;
  const shouldShowWaypointsSection =
    objectsVisibility[ViewObjectTypes.waypoints] && hasPanos;

  const streamCadModelOutOfCore = useAppSelector(
    selectHasFeature(Features.StreamCadModelOutOfCore),
  );

  const isAutomaticWaypointMobileScannersEnabled = useAppSelector(
    selectHasFeature(Features.AutomaticWaypointMobileScanners),
  );

  const isOdometryWaypointSpacingEnabled = useAppSelector(
    selectHasFeature(Features.OdometryWaypointSpacing),
  );
  return (
    <FaroMenu
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: -2,
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      onClose={onClose}
      // Overflow needs to be visible to not clip the labels of the FaroSlider's
      visibleOverflow
      dark
      aria-label="General view settings"
    >
      <Stack sx={{ gap: 1, p: 1, minWidth: "291px" }}>
        <FaroText
          variant="heading12"
          color={grey[100]}
          sx={{ textTransform: "uppercase", fontSize: "10px", py: 0.5 }}
        >
          General View Setting
        </FaroText>
        {/** Annotations section */}
        <ViewOptionSection
          label="Annotations"
          isChecked={objectsVisibility[ViewObjectTypes.annotations]}
          onToggled={(visible) => {
            updateObjectVisibility(
              visible,
              ViewObjectTypes.annotations,
              EventType.toggleAnnotationsVisibility,
            );
          }}
          aria-label="Annotations visibility toggle"
        />
        {/** Waypoints section */}
        <ViewOptionSection
          label="Waypoints"
          isChecked={objectsVisibility[ViewObjectTypes.waypoints]}
          onToggled={(visible) => {
            updateObjectVisibility(
              visible,
              ViewObjectTypes.waypoints,
              EventType.toggleWaypointsVisibility,
            );
          }}
          aria-label="Waypoints visibility toggle"
        >
          {objectsVisibility[ViewObjectTypes.waypoints] && (
            <ViewOptionSwitch
              label="Show labels"
              isChecked={showWaypointLabels}
              onToggled={(visible) => {
                setShowWaypointLabels(visible);
                updateObjectVisibility(
                  visible,
                  ViewObjectTypes.waypointLabels,
                  EventType.toggleWaypointLabelsVisibility,
                );
              }}
              aria-label="Waypoint labels visibility toggle"
            />
          )}
          {shouldShowWaypointsSection && (
            <>
              <ViewOptionSwitch
                label="Show waypoints on floors"
                isChecked={shouldShowWaypointsOnFloors}
                onToggled={toggleShouldShowWaypointsOnFloors}
                helpButton={<ShowWaypointsOnFloorsHelp />}
                aria-label="Show waypoints on floors toggle"
              />
              <WaypointsColorizationOptions
                disableColorByElevation={!waypointsAltitudeRange}
                disableColorByDate={!waypointsDateRange}
              />
            </>
          )}
        </ViewOptionSection>
        {/** Measurements section */}
        <ViewOptionSection
          label="Measurements"
          isChecked={objectsVisibility[ViewObjectTypes.measurements]}
          onToggled={(visible) => {
            updateObjectVisibility(
              visible,
              ViewObjectTypes.measurements,
              EventType.toggleMeasurementsVisibility,
            );
          }}
          aria-label="Measurements visibility toggle"
        />
        {/** Analyses section */}
        <ViewOptionSection
          label="Analyses"
          isChecked={objectsVisibility[ViewObjectTypes.analyses]}
          onToggled={(visible) => {
            updateObjectVisibility(
              visible,
              ViewObjectTypes.analyses,
              EventType.toggleAnalysesVisibility,
            );
          }}
        />
        {/** Trajectories section */}
        <ViewOptionSection
          label="Trajectories"
          isChecked={showTrajectories}
          onToggled={(visible) => {
            setShowTrajectories(visible);
            Analytics.track<ToggleTrajectoriesVisibilityProperties>(
              EventType.toggleTrajectoriesVisibility,
              { visible, numberOfTrajectories: paths.length },
            );
            dispatch(
              setObjectVisibility({
                type: ViewObjectTypes.trajectories,
                visibility: visible,
              }),
            );
          }}
          aria-label="Trajectories visibility toggle"
        >
          {isAutomaticWaypointMobileScannersEnabled &&
            isOdometryWaypointSpacingEnabled && (
              <OdometryWaypointSpacingSlider />
            )}
        </ViewOptionSection>
        <GuidedSplatsNavigationToggle />
        {/** 2D Overview orientation section */}
        <OrientationOf2dOverviewMenu />
        <Divider sx={{ borderColor: grey[800] }} />
        <VisibilityDistanceSlider />
        <Divider sx={{ borderColor: grey[800] }} />
        <PointCloudProfilesSlider />
        {streamCadModelOutOfCore && (
          <>
            <Divider sx={{ borderColor: grey[800] }} />
            <CadDistanceFilteringSlider />
          </>
        )}
      </Stack>
    </FaroMenu>
  );
}
